/* PlanReadinessScorecard — iter98.75
 *
 * Floating, collapsible scorecard pinned to the right edge of the
 * planner's workspace. Surfaces:
 *
 *   - Overall plan readiness % (from `usePlanCompletion`)
 *   - Section status chips (done / warn / empty)
 *   - Advisory issue list (from `usePlanIssues`) split into two tiers:
 *       · "would block in strict mode"  → red dots
 *       · "clinical caution"            → amber dots
 *
 * Each clickable item dispatches the existing `plan-section-open`
 * event (PlanEditorInline listens) AND scrolls the matching anchor
 * into view, so the planner jumps straight to the field that needs
 * attention.
 *
 * Visual states:
 *   - Collapsed → a single circular badge showing the score
 *   - Expanded  → 320px panel with sections + issues
 *
 * Empty state (overall 100%, no issues) → renders a green "Plan looks
 * solid" callout instead of the issue list.
 */
import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    CheckCircle, WarningCircle, Circle, CaretRight, X as XIcon,
    Gauge, Sparkle, ArrowRight,
} from "@phosphor-icons/react";

import usePlanCompletion from "@/components/case/usePlanCompletion";
import usePlanIssues from "@/components/case/usePlanIssues";


function jumpToSection({ sectionId, anchor }) {
    if (sectionId) {
        window.dispatchEvent(
            new CustomEvent("plan-section-open", { detail: { sectionId: anchor } })
        );
    }
    setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
}


function statusDot(status) {
    if (status === "done") return <CheckCircle size={12} weight="fill" className="text-emerald-500 shrink-0" />;
    if (status === "warn") return <WarningCircle size={12} weight="fill" className="text-amber-500 shrink-0" />;
    return <Circle size={12} weight="bold" className="text-muted-foreground/40 shrink-0" />;
}


export default function PlanReadinessScorecard({ caseDoc, files }) {
    const completion = usePlanCompletion(caseDoc, files);
    const { issues, strictBlockers, advisories } = usePlanIssues(caseDoc?.plan);
    const [expanded, setExpanded] = useState(false);
    // iter98.98 — Jump-to-next-blocker cursor. Cycles through every
    // `blocker-when-strict` issue in order, wrapping around. Each click
    // advances the cursor by 1 and scrolls/expands the target section
    // via the existing `plan-section-open` event.
    const cursorRef = useRef(0);
    const blockerIssues = issues.filter((i) => i.severity === "blocker-when-strict");

    const jumpToNextBlocker = (e) => {
        if (e && typeof e.stopPropagation === "function") e.stopPropagation();
        if (blockerIssues.length === 0) return;
        const idx = cursorRef.current % blockerIssues.length;
        const issue = blockerIssues[idx];
        cursorRef.current = idx + 1;
        jumpToSection({ sectionId: issue.sectionId, anchor: issue.anchor });
    };

    const score = completion.overallPct;
    const tone =
        score >= 90 ? "emerald" :
        score >= 60 ? "amber"   :
        "rose";
    const toneRing = {
        emerald: "ring-emerald-200 text-emerald-700 bg-emerald-50",
        amber:   "ring-amber-200 text-amber-700 bg-amber-50",
        rose:    "ring-rose-200 text-rose-700 bg-rose-50",
    }[tone];

    return (
        <div
            className="fixed right-3 lg:right-5 top-1/3 z-40 hidden md:block"
            data-testid="plan-readiness-scorecard"
        >
            <AnimatePresence initial={false} mode="wait">
                {!expanded ? (
                    <motion.div
                        key="collapsed"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 30 }}
                        transition={{ duration: 0.18 }}
                        className="flex flex-col items-end gap-1.5"
                    >
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            data-testid="scorecard-toggle-expand"
                            title={`Plan readiness: ${score}% · ${strictBlockers + advisories} advisory issue${strictBlockers + advisories === 1 ? "" : "s"}`}
                            className={`group relative inline-flex items-center gap-1.5 rounded-full border bg-card shadow-md hover:shadow-lg transition-shadow px-2.5 py-1.5 ring-1 ${toneRing}`}
                        >
                            <Gauge size={12} weight="duotone" />
                            <span className="font-display font-bold text-xs leading-none tabular-nums">
                                {score}%
                            </span>
                            {(strictBlockers + advisories) > 0 && (
                                <span
                                    className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-600 text-white text-[9px] font-bold grid place-items-center shadow"
                                    data-testid="scorecard-issue-count"
                                >
                                    {strictBlockers + advisories}
                                </span>
                            )}
                        </button>

                        {/* iter98.98 — Jump-to-next-blocker shortcut. Single
                            click anywhere on the page jumps to the next
                            unresolved blocker, scrolling + auto-opening its
                            accordion. Cycles through all blockers on repeat
                            clicks. Only renders when blockers > 0 so the
                            chrome stays clean when the plan is healthy. */}
                        {blockerIssues.length > 0 && (
                            <button
                                type="button"
                                onClick={jumpToNextBlocker}
                                data-testid="scorecard-jump-next-blocker"
                                title="Jump to the next unresolved blocker"
                                className="inline-flex items-center gap-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-bold px-2 py-1 shadow-md hover:shadow-lg transition-all uppercase tracking-wider"
                            >
                                <span>Next blocker</span>
                                <ArrowRight size={10} weight="bold" />
                                <span className="ml-0.5 text-[9px] opacity-90 tabular-nums">
                                    ({blockerIssues.length})
                                </span>
                            </button>
                        )}
                    </motion.div>
                ) : (
                    <motion.div
                        key="expanded"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 30 }}
                        transition={{ duration: 0.2 }}
                        className="w-[320px] max-h-[78vh] overflow-y-auto rounded-2xl border bg-card shadow-2xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b bg-secondary/40">
                            <div className="flex items-center gap-2">
                                <Gauge size={14} weight="duotone" className="text-primary" />
                                <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-foreground">
                                    Plan readiness
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setExpanded(false)}
                                data-testid="scorecard-toggle-collapse"
                                className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-secondary/60 transition-colors"
                                aria-label="Collapse scorecard"
                            >
                                <XIcon size={12} weight="bold" />
                            </button>
                        </div>

                        {/* Score header */}
                        <div className="px-4 py-4 border-b">
                            <div className="flex items-center gap-3">
                                <div className={`w-14 h-14 rounded-full grid place-items-center ring-2 ${toneRing}`}>
                                    <span className="font-display font-extrabold text-lg tabular-nums leading-none">
                                        {score}%
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold text-foreground">
                                        {completion.done} / {completion.sections.length} sections complete
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                        {strictBlockers > 0
                                            ? `${strictBlockers} would block in strict mode`
                                            : advisories > 0
                                            ? `${advisories} clinical advisor${advisories === 1 ? "y" : "ies"}`
                                            : "Looks solid — keep going"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Sections */}
                        <div className="px-4 py-3 border-b">
                            <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground mb-2">
                                Sections
                            </p>
                            <ul className="space-y-1" data-testid="scorecard-sections">
                                {completion.sections.map((s) => (
                                    <li key={s.id}>
                                        <button
                                            type="button"
                                            onClick={() => jumpToSection({ sectionId: s.id, anchor: s.id })}
                                            className="group w-full flex items-center justify-between gap-2 text-left rounded-md px-2 py-1.5 hover:bg-secondary/50 transition-colors"
                                            data-testid={`scorecard-section-${s.id}`}
                                        >
                                            <span className="flex items-center gap-2 min-w-0">
                                                {statusDot(s.status)}
                                                <span className="text-[12px] font-medium truncate">
                                                    {s.label}
                                                </span>
                                            </span>
                                            <CaretRight size={10} weight="bold" className="text-muted-foreground/50 group-hover:text-foreground transition-colors shrink-0" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Issues */}
                        <div className="px-4 py-3">
                            <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Sparkle size={11} weight="fill" className="text-primary" />
                                Advisory issues
                            </p>
                            {issues.length === 0 ? (
                                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-800 flex items-center gap-2">
                                    <CheckCircle size={12} weight="fill" className="text-emerald-500 shrink-0" />
                                    Plan looks solid — no advisory issues.
                                </div>
                            ) : (
                                <ul className="space-y-1" data-testid="scorecard-issues">
                                    {issues.map((iss) => (
                                        <li key={iss.id}>
                                            <button
                                                type="button"
                                                onClick={() => jumpToSection({ sectionId: iss.sectionId, anchor: iss.anchor })}
                                                className="group w-full flex items-start gap-2 text-left rounded-md px-2 py-1.5 hover:bg-secondary/50 transition-colors"
                                                data-testid={`scorecard-issue-${iss.id}`}
                                            >
                                                <span
                                                    className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                                                        iss.severity === "blocker-when-strict"
                                                            ? "bg-rose-500"
                                                            : "bg-amber-500"
                                                    }`}
                                                />
                                                <span className="text-[11px] leading-snug text-foreground/85 group-hover:text-foreground">
                                                    {iss.label}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
